import { Box, Slider, TextField, Typography, useTheme } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import tinycolor from 'tinycolor2'
import ColorSelector from '../../../../colorSelector/colorSelector'
import {
    buildCustomMultiStrikeColorPalette,
    getDefaultMultiStrikeColorPalette,
    MAX_CUSTOM_HUE_SHIFT,
    MIN_CUSTOM_HUE_SHIFT,
    MULTI_STRIKE_PALETTE_SIZE,
    MultiStrikeColorPaletteId,
    normalizeMultiStrikeColorPaletteId,
} from '../../../multiStrikeColorPalettes'

type Props = {
    colorPaletteId: MultiStrikeColorPaletteId | 'vivid' | 'soft'
    customCallBaseColor: string
    customPutBaseColor: string
    customCallHueShift: number
    customPutHueShift: number
    onPaletteChange: (paletteId: MultiStrikeColorPaletteId) => void
    onCustomCallBaseColorChange: (color: string) => void
    onCustomPutBaseColorChange: (color: string) => void
    onCustomCallHueShiftChange: (hueShift: number) => void
    onCustomPutHueShiftChange: (hueShift: number) => void
}

const PaletteColorStrip = ({ colors, maxColors = MULTI_STRIKE_PALETTE_SIZE }: { colors: string[]; maxColors?: number }) => (
    <Box sx={{ display: 'flex', gap: 0.25 }}>
        {colors.slice(0, maxColors).map((color, index) => (
            <Box
                key={`${color}-${index}`}
                sx={{
                    width: 14,
                    height: 8,
                    borderRadius: 0.5,
                    backgroundColor: color,
                }}
            />
        ))}
    </Box>
)

const clampHueShift = (value: number) => {
    if (Number.isNaN(value)) {
        return MIN_CUSTOM_HUE_SHIFT
    }
    return Math.min(MAX_CUSTOM_HUE_SHIFT, Math.max(MIN_CUSTOM_HUE_SHIFT, value))
}

const ColorHueRow = ({
    label,
    color,
    opacity,
    hueShift,
    onColorChange,
    onHueShiftChange,
}: {
    label: string
    color: string
    opacity: number
    hueShift: number
    onColorChange: (color: string, transparency: number) => void
    onHueShiftChange: (value: number) => void
}) => {
    const handleHueInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onHueShiftChange(clampHueShift(Number(event.target.value)))
        },
        [onHueShiftChange]
    )

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography sx={{ width: 56, fontSize: '13px', flexShrink: 0 }}>{label}</Typography>
            <ColorSelector
                compact
                showOnlySwatch
                color={color}
                opacity={opacity}
                onColorChange={onColorChange}
            />
            <Typography sx={{ fontSize: '13px', flexShrink: 0, color: 'text.secondary' }}>
                Hue
            </Typography>
            <Slider
                size="small"
                value={hueShift}
                min={MIN_CUSTOM_HUE_SHIFT}
                max={MAX_CUSTOM_HUE_SHIFT}
                step={5}
                onChange={(_event, nextValue) => onHueShiftChange(nextValue as number)}
                sx={{ flex: 1, mx: 0.5 }}
            />
            <TextField
                size="small"
                type="number"
                variant="standard"
                value={hueShift}
                onChange={handleHueInputChange}
                inputProps={{
                    min: MIN_CUSTOM_HUE_SHIFT,
                    max: MAX_CUSTOM_HUE_SHIFT,
                    step: 5,
                }}
                sx={{
                    width: 32,
                    flexShrink: 0,
                    '& .MuiInput-root': {
                        fontSize: '12px',
                    },
                    '& input': {
                        py: 0,
                        px: 0,
                        textAlign: 'right',
                    },
                    '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                        display: 'none',
                    },
                }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, width: 10 }}>
                °
            </Typography>
        </Box>
    )
}

const MultiStrikeColorPalettePicker = ({
    colorPaletteId,
    customCallBaseColor,
    customPutBaseColor,
    customCallHueShift,
    customPutHueShift,
    onPaletteChange,
    onCustomCallBaseColorChange,
    onCustomPutBaseColorChange,
    onCustomCallHueShiftChange,
    onCustomPutHueShiftChange,
}: Props) => {
    const theme = useTheme()
    const selectedPaletteId = normalizeMultiStrikeColorPaletteId(colorPaletteId)
    const activeThemeMode = theme.palette.mode === 'dark' ? 'dark' : 'light'
    const defaultPalette = useMemo(
        () => getDefaultMultiStrikeColorPalette(activeThemeMode),
        [activeThemeMode]
    )
    const customPaletteConfig = useMemo(
        () => ({
            customCallBaseColor,
            customPutBaseColor,
            customCallHueShift,
            customPutHueShift,
        }),
        [customCallBaseColor, customCallHueShift, customPutBaseColor, customPutHueShift]
    )
    const customPalette = useMemo(
        () => buildCustomMultiStrikeColorPalette(customPaletteConfig),
        [customPaletteConfig]
    )
    const callBaseTiny = useMemo(() => tinycolor(customCallBaseColor), [customCallBaseColor])
    const putBaseTiny = useMemo(() => tinycolor(customPutBaseColor), [customPutBaseColor])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
                onClick={() => onPaletteChange('default')}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: 1,
                    borderColor: selectedPaletteId === 'default' ? 'primary.main' : 'divider',
                    '&:hover': {
                        borderColor: 'primary.main',
                    },
                }}
            >
                <Typography sx={{ width: 56, fontSize: '13px', flexShrink: 0 }}>
                    Default
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                    <PaletteColorStrip colors={defaultPalette.callColors} />
                    <PaletteColorStrip colors={defaultPalette.putColors} />
                </Box>
            </Box>

            <Box
                sx={{
                    borderRadius: 1,
                    border: 1,
                    borderColor: selectedPaletteId === 'custom' ? 'primary.main' : 'divider',
                    overflow: 'hidden',
                }}
            >
                <Box
                    onClick={() => onPaletteChange('custom')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1,
                        py: 0.75,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    <Typography sx={{ width: 56, fontSize: '13px', flexShrink: 0 }}>
                        Custom
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                        <PaletteColorStrip colors={customPalette.callColors} />
                        <PaletteColorStrip colors={customPalette.putColors} />
                    </Box>
                </Box>

                {selectedPaletteId === 'custom' && (
                    <Box
                        sx={{ px: 1, pb: 1, pt: 0.25, display: 'flex', flexDirection: 'column', gap: 1 }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <ColorHueRow
                            label="CE"
                            color={callBaseTiny.toHexString()}
                            opacity={callBaseTiny.getAlpha()}
                            hueShift={customCallHueShift}
                            onColorChange={(color, transparency) => {
                                onCustomCallBaseColorChange(tinycolor(color).setAlpha(transparency).toHex8String())
                            }}
                            onHueShiftChange={onCustomCallHueShiftChange}
                        />
                        <ColorHueRow
                            label="PE"
                            color={putBaseTiny.toHexString()}
                            opacity={putBaseTiny.getAlpha()}
                            hueShift={customPutHueShift}
                            onColorChange={(color, transparency) => {
                                onCustomPutBaseColorChange(tinycolor(color).setAlpha(transparency).toHex8String())
                            }}
                            onHueShiftChange={onCustomPutHueShiftChange}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default memo(MultiStrikeColorPalettePicker)
